import React, { useReducer, useEffect } from 'react';

import { css } from '@compiled/react';
import Spinner from '@atlaskit/spinner/spinner';
import { fg } from '@confluence/feature-gating';

import { useStaticMacroStoreContext } from './StaticMacroStoreContext.tsx';

const spinnerWrapperStyles = css({ overflow: 'hidden' });

type ForgeMacroProps = {
	localId?: string;
	extensionKey?: string;
	renderIframe: () => React.ReactElement;
};

export function createForgeMacroExtensionHandler(isEditing: boolean) {
	if (!fg('conf_frontend_forge_static_macro_mvp') || isEditing) {
		return undefined;
	}
	return (node: any, renderFn: (param: { references: any[] }) => JSX.Element): JSX.Element => (
		<ForgeMacro
			localId={node?.parameters?.localId}
			extensionKey={node?.extensionKey?.split('/').at(-1)}
			renderIframe={() => renderFn({ references: [] })}
		/>
	);
}

export function ForgeMacro({ localId, extensionKey, renderIframe }: ForgeMacroProps) {
	const ctx = useStaticMacroStoreContext();
	const [, forceUpdate] = useReducer((n: number) => n + 1, 0);

	// If the extension type doesn't support staticView, render iframe immediately — no waiting
	const isStatic = ctx && extensionKey ? ctx.isStaticExtension(extensionKey) : false;

	useEffect(() => {
		if (!ctx || !localId || !isStatic) return;
		const unsubscribe = ctx.subscribe(localId, forceUpdate);
		// Data may have already arrived before we subscribed — check immediately
		const store = ctx.storeRef.current;
		if (store?.html.has(localId) || store?.errors.has(localId) || store?.fullyLoaded) {
			forceUpdate();
		}
		return unsubscribe;
	}, [ctx, localId, forceUpdate, isStatic]);

	if (!ctx || !localId || !isStatic) {
		return renderIframe();
	}

	const store = ctx.storeRef.current;
	if (!store) {
		return renderIframe();
	}
	const html = store.html.get(localId);
	const errorCode = store.errors.get(localId);

	if (html) {
		return (
			<div
				// eslint-disable-next-line react/no-danger
				dangerouslySetInnerHTML={{ __html: html }}
			/>
		);
	}

	if (errorCode) {
		return renderIframe();
	}

	return store.fullyLoaded ? (
		renderIframe()
	) : (
		<div css={spinnerWrapperStyles}>
			<Spinner size="small" />
		</div>
	);
}
